import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, ArrowRight, Check, Warning, Camera, Lightning, WifiHigh, Wrench, Printer } from '@phosphor-icons/react';
import { SEO } from '../components/SEO';

const SECTIONS = [
  {
    title: 'Before you touch a wire',
    icon: Camera,
    color: 'text-sky-500',
    items: [
      { t: 'Photograph every wire terminal on the old controller (front + angled).', detail: 'Include a shot of any sensor wires in the S1/S2 terminal.' },
      { t: 'Label each zone wire with masking tape.', detail: 'Z1, Z2, … Zn, plus C (common), M/MV (master valve), S1 (sensor). Rachio includes labels but they peel over years.' },
      { t: 'Note the running program schedule.', detail: 'Start times, zone durations, days. You\'ll regenerate smarter, but good to compare.' },
      { t: 'Check your Wi-Fi band.', detail: 'Rachio 3 requires 2.4 GHz. If you run Unifi/mesh with band steering, temporarily broadcast a 2.4 GHz SSID or use the Unifi "Advanced → Radio" settings to allow 2.4 GHz clients on your main SSID.' },
      { t: 'Kill the breaker feeding the controller.', detail: 'Turn it off at the panel. Verify with a voltage tester or just try powering the old controller back on — nothing.' },
    ],
  },
  {
    title: 'Unbox + mount',
    icon: Lightning,
    color: 'text-amber-500',
    items: [
      { t: 'Verify box contents.', detail: 'Controller, wire labels, 2× masonry anchors, 2× screws, mounting template, setup card.' },
      { t: 'Tape the mounting template to the wall.', detail: 'Level matters — ~5 ft from floor, close to the old controller\'s footprint.' },
      { t: 'Pre-drill holes (1/4" bit for masonry anchors).', detail: 'Indoor drywall: use drywall anchors if no stud.' },
      { t: 'Mount the back plate.', detail: 'Screws snug but not crushing. Plate should feel solid.' },
    ],
  },
  {
    title: 'Wire it up',
    icon: Wrench,
    color: 'text-emerald-500',
    items: [
      { t: 'Confirm breaker still off.', detail: 'Last chance to verify.' },
      { t: 'Route wires through the bottom cutout.', detail: 'Strip 3/8" if needed. Twist strands together if they\'re frayed.' },
      { t: 'Common (C) into C terminal.', detail: 'Usually white. Rachio uses a single C terminal — all commons go here.' },
      { t: 'Master valve / pump relay (M) into M terminal.', detail: 'Only if you have one. Most residential systems don\'t.' },
      { t: 'Rain sensor (S1/S2) into Rachio\'s sensor port.', detail: 'Optional. Rachio\'s weather intelligence usually replaces the physical sensor\'s logic.' },
      { t: 'Zone wires into 1, 2, 3, …', detail: 'Match each label to its terminal. Tighten screws firmly.' },
      { t: 'Double-check no strands are touching across terminals.', detail: 'Shorts will cause false zone triggers. Trim any stray whiskers.' },
      { t: 'Plug in AC adapter last.', detail: 'Cable enters the bottom of the back plate.' },
    ],
  },
  {
    title: 'Power + pair',
    icon: WifiHigh,
    color: 'text-violet-500',
    items: [
      { t: 'Flip breaker back on.', detail: 'LED on Rachio should glow. Typically green/blue.' },
      { t: 'Install the Rachio app.', detail: 'iOS App Store / Android Play Store. Create account or log in.' },
      { t: 'Tap "Add a controller" → select Rachio 3.', detail: 'App prompts you to hold your phone close. Bluetooth pairing first, then Wi-Fi.' },
      { t: 'Select your 2.4 GHz SSID.', detail: 'If your network only shows 5 GHz, go temporarily broadcast 2.4 GHz on Unifi and retry.' },
      { t: 'Name zones to match real yard locations.', detail: '"Front Lawn," "North Bed," "Pool Pots" — not "Zone 1."' },
      { t: 'Set zone properties.', detail: 'Nozzle type, soil, sun exposure. Rachio uses these for Flex Daily — we override with our own Water Budget math.' },
    ],
  },
  {
    title: 'Verify + connect TurfPulse',
    icon: Check,
    color: 'text-primary',
    items: [
      { t: 'Run each zone for 30 seconds manually.', detail: 'Walk the yard. Confirm right head set activates. Catch bad wiring before it hides.' },
      { t: 'Get your Personal Access Token.', detail: 'Rachio web app → Account Settings → Get API Key. Copy it.' },
      { t: 'Paste token into TurfPulse Rachio Connect.', detail: 'Opens live zone data. Translate your Water Budget → Program A/B/C cheat sheet.' },
      { t: 'Enter the cheat-sheet schedules into the Rachio app.', detail: 'Create Fixed schedules (not Flex) so the physics math stays in charge.' },
      { t: 'Disable Flex Daily if Rachio enabled it by default.', detail: 'Otherwise Rachio\'s defaults fight your Water Budget schedule.' },
    ],
  },
  {
    title: 'File the rebate',
    icon: Printer,
    color: 'text-emerald-500',
    items: [
      { t: 'Save your Amazon/retailer receipt PDF.', detail: 'Round Rock needs itemized: date, vendor, amount, description.' },
      { t: 'Open TurfPulse Rebate Helper.', detail: 'Generates the application PDF + email draft in 60 seconds.' },
      { t: 'Email to jwoods@roundrocktexas.gov.', detail: 'Within 90 days of purchase. Include receipt + generated PDF.' },
      { t: 'Check arrives 2–4 weeks later.', detail: 'If they want a post-install inspection, they\'ll contact you first.' },
    ],
  },
];

const InstallChecklistPage = () => {
  const navigate = useNavigate();
  const [checked, setChecked] = useState({});

  const toggle = (k) => setChecked((c) => ({ ...c, [k]: !c[k] }));

  const totalItems = SECTIONS.reduce((a, s) => a + s.items.length, 0);
  const completed = Object.values(checked).filter(Boolean).length;
  const pct = Math.round((completed / totalItems) * 100);

  return (
    <div className="min-h-screen bg-background" data-testid="install-checklist-page">
      <SEO title="Rachio 3 Install Checklist — TurfPulse" description="Pre-flight checklist for replacing a Rainbird with a Rachio 3. Labels, wiring, WiFi, app connect, rebate." />

      <header className="border-b border-border/60 bg-card/80 backdrop-blur-sm sticky top-0 z-30 print:hidden">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-4 py-3">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="install-back-btn">
            <ArrowLeft size={14} /> Back
          </button>
          <div className="text-sm font-bold">Rachio 3 Install Checklist</div>
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="install-print-btn">
            <Printer size={14} className="mr-1" /> Print
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium print:hidden">
            <Wrench size={12} weight="fill" /> 20-minute install · 6 sections · {totalItems} checks
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Don't let a camera catch you fumbling WiFi pairing.</h1>
          <p className="text-base text-muted-foreground">
            Run through this once before you unbox — especially the Wi-Fi pre-flight. Rachio 3 swaps 1:1 with Rainbird wiring, but 2.4 GHz networking is where most installs stall.
          </p>
          <div className="flex items-center gap-3 pt-2 print:hidden">
            <div className="h-2 flex-1 bg-muted/50 rounded-full overflow-hidden">
              <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} data-testid="install-progress-bar" />
            </div>
            <span className="text-xs font-mono text-muted-foreground" data-testid="install-progress-text">{completed}/{totalItems} · {pct}%</span>
          </div>
        </div>

        {SECTIONS.map((sec, si) => {
          const Icon = sec.icon;
          return (
            <Card key={si} data-testid={`install-section-${si}`} className="break-inside-avoid">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Icon size={18} weight="fill" className={sec.color} /> {sec.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {sec.items.map((item, ii) => {
                  const key = `${si}-${ii}`;
                  return (
                    <label key={key} className="flex items-start gap-3 py-1.5 border-b border-border/40 last:border-0 cursor-pointer group" data-testid={`install-item-${si}-${ii}`}>
                      <input
                        type="checkbox"
                        checked={!!checked[key]}
                        onChange={() => toggle(key)}
                        className="mt-0.5 h-4 w-4 shrink-0 rounded border-border text-primary focus:ring-primary"
                        data-testid={`install-check-${si}-${ii}`}
                      />
                      <div className={`flex-1 ${checked[key] ? 'line-through opacity-60' : ''}`}>
                        <div className="text-sm font-medium">{item.t}</div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">{item.detail}</div>
                      </div>
                    </label>
                  );
                })}
              </CardContent>
            </Card>
          );
        })}

        <Card className="border-amber-500/40 bg-amber-500/5 print:hidden">
          <CardContent className="p-4 flex items-start gap-3">
            <Warning size={20} weight="fill" className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="text-xs space-y-1">
              <div className="font-semibold">Unifi gotcha</div>
              <p className="text-muted-foreground">
                If your Unifi SSID is running band-steering (2.4 + 5 on one name), the Rachio pairing will fail because the controller only sees the 5 GHz advertisement. Temporarily create a <strong>2.4 GHz-only SSID</strong> (Network → Wi-Fi → Add Network → Advanced → Radios = 2.4 GHz only), pair, then you can disable it.
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-col sm:flex-row gap-2 pt-4 print:hidden">
          <Button className="flex-1" onClick={() => navigate('/rachio/connect')} data-testid="install-next-connect">
            Next: connect to TurfPulse <ArrowRight size={14} className="ml-1" />
          </Button>
          <Button variant="outline" className="flex-1" onClick={() => navigate('/rachio/rebate-helper')} data-testid="install-next-rebate">
            File rebate
          </Button>
        </div>
      </main>

      <style>{`@media print{body{background:white} header,footer{display:none}}`}</style>
    </div>
  );
};

export default InstallChecklistPage;
